import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import useApprovalView from '@/hooks/approval/useApprovalView';
import type { RelatedApprovalItem } from '@/types/approval';
import {
  buildCancelSubject,
  isCancelOriginOf,
  type CancelTargetSnapshot,
} from '@/utils/approval/cancelApproval';
import { formatApprovalDate } from '@/utils/approval/formatApprovalDate';
import { normalizeLang, pickI18nText } from '@/utils/approval/i18nText';
import { getApprovalDocUrl } from '@/utils/urls';
import type { ApprovalFormValues } from '../types';

export type CancelTargetStatus =
  | 'ready'
  | 'loading'
  /** 진입 쿼리에 원 품의가 없음 */
  | 'missing'
  /** 404 또는 200 + data:null */
  | 'not-found'
  /** 원 품의 유형이 이 취소 양식의 대상이 아님 */
  | 'mismatch'
  | 'error';

type Params = {
  apvType: number;
  /** 진입 쿼리 `doc` — 신규 작성에서만 옴 */
  entryDocId?: string;
  /** 편집 진입 = 단건조회 attributes 복원값. 있으면 재조회 없이 그대로 */
  initialTarget?: CancelTargetSnapshot | null;
  isEditMode: boolean;
};

/**
 * 취소 대상 원 품의 확정 — 진입 `doc` 를 v2 단건조회로 읽어 유형 검증 후 RHF cancelTarget 에 스냅샷.
 * 신규 작성은 성공 시 제목 자동값(사용자 입력 있으면 유지) + 상위 연관품의 자동 추가(레거시 동작).
 * 스냅샷이 이미 있으면(편집 복원·첫 저장 뒤 재렌더) 조회하지 않음
 */
const useCancelTargetField = ({
  apvType,
  entryDocId,
  initialTarget,
  isEditMode,
}: Params) => {
  const { t, i18n } = useTranslation();
  const { setValue, getValues, watch } = useFormContext<ApprovalFormValues>();
  const cancelTarget = watch('cancelTarget');

  useEffect(() => {
    if (!initialTarget || getValues('cancelTarget')) return;
    setValue('cancelTarget', initialTarget, { shouldDirty: false });
  }, [initialTarget, getValues, setValue]);

  const fetchDocId =
    !cancelTarget && !initialTarget && entryDocId ? entryDocId : null;
  const { data, error, isLoading } = useApprovalView(fetchDocId);

  const appliedDocIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!fetchDocId || !data || !isCancelOriginOf(apvType, data.type.code))
      return;
    if (appliedDocIdRef.current === fetchDocId) return;
    appliedDocIdRef.current = fetchDocId;

    setValue(
      'cancelTarget',
      {
        approvalId: data.documentId,
        subject: data.subject ?? '',
        // authoredAt 은 UTC ISO — 문자열 앞자리를 자르면 KST 자정 전후 하루가 밀림. 서버 submittedAt(KST 날짜)과 맞춤
        submittedAt: formatApprovalDate(data.authoredAt) ?? '',
        url: getApprovalDocUrl(data.documentId),
      },
      { shouldDirty: false },
    );
    if (isEditMode) return;

    if (!getValues('subject')?.trim()) {
      setValue('subject', buildCancelSubject(data.subject ?? ''), {
        shouldDirty: false,
      });
    }

    const related = getValues('relatedApprovals');
    const alreadyLinked = [
      ...related.parents,
      ...related.children,
      ...related.related,
    ].some((item) => item.docId === data.documentId);
    if (alreadyLinked) return;
    const lang = normalizeLang(i18n.language);
    const item: RelatedApprovalItem = {
      docId: data.documentId,
      subject: data.subject ?? '',
      status: data.status,
      author: { name: pickI18nText(data.author?.name, lang) },
      authoredAt: data.authoredAt,
    };
    setValue('relatedApprovals.parents', [...related.parents, item], {
      shouldDirty: false,
    });
  }, [
    fetchDocId,
    data,
    apvType,
    isEditMode,
    getValues,
    setValue,
    i18n.language,
  ]);

  const status: CancelTargetStatus = useMemo(() => {
    if (cancelTarget) return 'ready';
    if (!fetchDocId) return 'missing';
    if (error) {
      const httpStatus =
        error instanceof AxiosError ? error.response?.status : undefined;
      return httpStatus === 404 ? 'not-found' : 'error';
    }
    if (isLoading || data === undefined) return 'loading';
    if (data === null) return 'not-found';
    if (!isCancelOriginOf(apvType, data.type.code)) return 'mismatch';
    return 'loading';
  }, [cancelTarget, fetchDocId, error, isLoading, data, apvType]);

  const message = useMemo(() => {
    switch (status) {
      case 'missing':
        return t('취소할 품의 정보가 없습니다.');
      case 'not-found':
        return t('품의 정보를 찾을 수 없습니다.');
      case 'mismatch':
        return t('취소할 수 없는 품의입니다.');
      case 'error':
        return t('데이터를 불러오지 못했습니다.');
      case 'loading':
        return t('취소할 품의 정보를 불러오는 중입니다…');
      default:
        return null;
    }
  }, [status, t]);

  return { cancelTarget, status, message };
};

export default useCancelTargetField;
